import { useState } from "react";
import { NavLink, Outlet, useLocation, Navigate } from "react-router-dom";
import { LayoutDashboard, ShieldAlert, ClipboardCheck, FileText, FolderOpen, Briefcase, Package, CreditCard, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";
import { api } from "@/lib/api";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, testid: "app-nav-dashboard", end: true },
  { to: "/app/risks", label: "Risk Register", icon: ShieldAlert, testid: "app-nav-risks" },
  { to: "/app/audits", label: "Audits", icon: ClipboardCheck, testid: "app-nav-audits" },
  { to: "/app/sops", label: "SOP Library", icon: FileText, testid: "app-nav-sops" },
  { to: "/app/documents", label: "Document Center", icon: FolderOpen, testid: "app-nav-documents" },
  { to: "/app/services", label: "TSM Services", icon: Briefcase, testid: "app-nav-services" },
  { to: "/app/toolkit", label: "Toolkit", icon: Package, testid: "app-nav-toolkit" },
  { to: "/app/billing", label: "Billing & Plan", icon: CreditCard, testid: "app-nav-billing" },
];

export const AppShell = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  if (user.role === "admin") return <Navigate to="/admin" replace />;

  if (!user.onboarding_completed && location.pathname !== "/app/onboarding") {
    return <Navigate to="/app/onboarding" replace />;
  }

  const signOut = async () => {
    await api.post("/api/auth/logout");
    window.location.href = "/";
  };

  const sidebar = (
    <div className="flex h-full flex-col bg-navydeep text-white">
      <div className="flex items-center gap-3 border-b border-white/10 px-6 py-5">
        <LogoMark className="h-9 w-9" />
        <div className="leading-tight">
          <p className="font-serif text-lg font-semibold">MITIVISE</p>
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-gold">Trust · Systems · Management</p>
        </div>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto p-4" data-testid="app-sidebar-nav">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-sm px-4 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-gold text-navy" : "text-white/70 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <p className="truncate px-2 text-sm font-semibold" data-testid="app-user-business">{user.business_name}</p>
        <p className="truncate px-2 text-xs text-white/50">{user.email}</p>
        <button
          type="button"
          onClick={signOut}
          data-testid="app-signout-button"
          className="mt-3 flex w-full items-center gap-2 rounded-sm border border-white/15 px-4 py-2 text-sm text-white/70 transition-colors hover:border-gold hover:text-gold"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-paper">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 lg:block">{sidebar}</aside>

      <div className="sticky top-0 z-30 flex items-center justify-between border-b border-navy/10 bg-white/90 px-4 py-3 backdrop-blur lg:hidden">
        <span className="flex items-center gap-2">
          <LogoMark className="h-8 w-8" />
          <span className="font-serif font-semibold text-navy">MITIVISE</span>
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="app-mobile-menu-button"
          className="rounded-sm border border-navy/15 p-2 text-navy"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-navy/60" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72">{sidebar}</div>
        </div>
      )}

      <main className="p-5 lg:ml-64 lg:p-10" data-testid="app-main">
        <Outlet />
      </main>
    </div>
  );
};

export const AdminShell = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user) return null;

  const signOut = async () => {
    await api.post("/api/auth/logout");
    window.location.href = "/";
  };

  const links = [
    { to: "/admin", label: "Dashboard", end: true, testid: "admin-nav-dashboard" },
    { to: "/admin/orders", label: "Orders", testid: "admin-nav-orders" },
    { to: "/admin/customers", label: "Customers", testid: "admin-nav-customers" },
    { to: "/admin/requests", label: "Service Requests", testid: "admin-nav-requests" },
    { to: "/admin/leads", label: "Leads & Interest", testid: "admin-nav-leads" },
    { to: "/admin/workshops", label: "Workshops", testid: "admin-nav-workshops" },
    { to: "/admin/config", label: "Pricing & Config", testid: "admin-nav-config" },
  ];

  return (
    <div className="min-h-screen bg-paper">
      <header className="sticky top-0 z-40 border-b border-black/5 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3">
          <span className="flex items-center gap-3">
            <LogoMark className="h-9 w-9" />
            <div className="leading-tight">
              <p className="font-serif text-lg font-semibold text-navy">MITIVISE Admin</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-golddark">TSM Group CLE</p>
            </div>
          </span>
          <nav className="hidden gap-6 md:flex" data-testid="admin-nav">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={l.testid}
                className={({ isActive }) => `text-sm font-semibold ${isActive ? "text-golddark" : "text-navy/60 hover:text-navy"}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a href="/" className="hidden text-sm font-semibold text-navy/60 hover:text-navy sm:block">View website</a>
            <button type="button" onClick={signOut} data-testid="admin-signout-button" className="rounded-sm border border-navy/15 px-4 py-2 text-sm font-semibold text-navy hover:border-gold">
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl p-5 lg:p-10" data-testid="admin-main">
        <Outlet />
      </main>
    </div>
  );
};
